import { useMemo } from "react";
import { FilterConfig, DropdownOption, SelectOptionsHook } from "@coherehealth/common";
import { ReferenceDataItem } from "@coherehealth/qm-api";
import { FIELD_REGISTRY, FieldSpec, FormValues, UserCtx } from "./fieldRegistry";

// Extends the standard MultiSelect FilterConfig to carry a lazy useOptions hook for
// the assignee field. SearchPanel reads this field via a duck-type cast and passes it
// to FilterDropdownWithSearch, keeping the FilterConfig type from @coherehealth/common unchanged.
export type LazyMultiSelectConfig = FilterConfig & {
  useOptions?: SelectOptionsHook<DropdownOption>;
};

export interface SearchFilterDeps {
  healthPlanOptions: ReferenceDataItem[];
  delegatedVendorOptions: ReferenceDataItem[];
  caseTypeOptions: ReferenceDataItem[];
  caseStatusOptions: ReferenceDataItem[];
  outcomeOptions: ReferenceDataItem[];
  useAssigneeOptions: SelectOptionsHook<DropdownOption>;
}

export interface ResolvedField {
  spec: FieldSpec;
  filterConfig: FilterConfig;
  disabled: boolean;
}

function refDataToDropdownOptions(items: ReferenceDataItem[]): DropdownOption[] {
  return items.map((item) => ({
    id: item.value || "",
    label: item.label || "",
  }));
}

function extractStringValues(formValues: FormValues): FormValues {
  const extracted: FormValues = {};
  for (const [key, value] of Object.entries(formValues)) {
    if (
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === "object" &&
      value[0] !== null &&
      "id" in value[0]
    ) {
      extracted[key] = value.map((v: DropdownOption) => v.id);
    } else if (
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      "id" in (value as Record<string, unknown>)
    ) {
      extracted[key] = (value as DropdownOption).id;
    } else {
      extracted[key] = value;
    }
  }
  return extracted;
}

function resolveOptions(spec: FieldSpec, deps: SearchFilterDeps): DropdownOption[] {
  if (!spec.options) {
    return [];
  }
  if ("static" in spec.options) {
    return spec.options.static as DropdownOption[];
  }
  const hookKey = spec.options.hook;
  const items = deps[hookKey];
  if (Array.isArray(items)) {
    return refDataToDropdownOptions(items as ReferenceDataItem[]);
  }
  return [];
}

function specToFilterConfig(spec: FieldSpec, deps: SearchFilterDeps, options: DropdownOption[]): FilterConfig {
  const base = {
    key: spec.id,
    label: spec.label,
    apiParamName: spec.apiParamName,
  };

  switch (spec.filterType) {
    case "MultiSelect":
    case "SearchableMultiSelect": {
      const config: LazyMultiSelectConfig = {
        ...base,
        filterType: "MultiSelect" as const,
        options,
        // Assignee uses lazy server-side loading; other SearchableMultiSelect fields stay static.
        ...(spec.id === "assignee" ? { useOptions: deps.useAssigneeOptions } : {}),
      };
      return config;
    }
    case "SingleSelect":
      return { ...base, filterType: "SingleSelect" as const, options };
    case "DatePicker":
      return { ...base, filterType: "DatePicker" as const };
    case "TextField":
      return { ...base, filterType: "TextField" as const };
    default:
      return { ...base, filterType: "TextField" as const };
  }
}

export function useSearchFilterConfigs(deps: SearchFilterDeps, formValues: FormValues, ctx: UserCtx): ResolvedField[] {
  return useMemo(() => {
    const extractedValues = extractStringValues(formValues);

    return FIELD_REGISTRY.filter((spec) => {
      if (spec.visibleWhen) {
        return spec.visibleWhen(extractedValues, ctx);
      }
      return true;
    }).map((spec) => {
      const options = resolveOptions(spec, deps);
      const filterConfig = specToFilterConfig(spec, deps, options);
      const disabled =
        (spec.disabledWhen ? spec.disabledWhen(extractedValues, ctx) : false) ||
        (Boolean(spec.disabledWhenNoOptions) && options.length === 0);

      return { spec, filterConfig, disabled };
    });
  }, [deps, formValues, ctx]);
}
